import { useCallback, useState } from "react";
import moment from "moment";

// =======================================================
// useReschedule
// -------------------------------------------------------
// Manages appointment rescheduling state.
//
// Responsibilities:
// - Store the appointment being moved
// - Store the selected target day
// - Store the new appointment time
// - Open and close the reschedule modal
//
// API calls and calendar refreshes remain inside Home.js
// during the first migration step.
// =======================================================

const useReschedule = () => {
  const [rescheduleMode, setRescheduleMode] = useState(false);
  const [appointmentToMove, setAppointmentToMove] = useState(null);
  const [rescheduleModalIsOpen, setRescheduleModalIsOpen] =
    useState(false);
  const [targetDay, setTargetDay] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState("");

  // =======================================================
  // Start Reschedule Mode
  // -------------------------------------------------------
  // Stores the selected appointment and prepares its current
  // time as the default reschedule time.
  // =======================================================

  const startRescheduleMode = useCallback((appointment, dayId) => {
    setAppointmentToMove({
      ...appointment,
      oldDayId: dayId,
    });

    setRescheduleTime(
      appointment.time
        ? moment(appointment.time, "HH:mm:ss").format("HH:mm")
        : ""
    );

    setRescheduleMode(true);
  }, []);

  // =======================================================
  // Cancel Reschedule Mode
  // -------------------------------------------------------
  // Resets all reschedule state and closes the modal.
  // =======================================================

  const cancelRescheduleMode = useCallback(() => {
    setRescheduleMode(false);
    setAppointmentToMove(null);
    setTargetDay(null);
    setRescheduleModalIsOpen(false);
    setRescheduleTime("");
  }, []);

  // =======================================================
  // Open Reschedule Modal
  // -------------------------------------------------------
  // Stores the selected target day and opens the modal.
  // =======================================================

  const openRescheduleModal = useCallback(
    (day, artistName) => {
      if (!rescheduleMode || !appointmentToMove) return;

      setTargetDay({
        dayId: day.dayId,
        day: day.day,
        artistName,
      });

      setRescheduleModalIsOpen(true);
    },
    [rescheduleMode, appointmentToMove]
  );

  return {
    rescheduleMode,
    appointmentToMove,
    rescheduleModalIsOpen,
    targetDay,
    rescheduleTime,
    setRescheduleTime,
    startRescheduleMode,
    cancelRescheduleMode,
    openRescheduleModal,
  };
};

export default useReschedule;